import { QATAR_DIAL_CODE } from '@fe/shared/Countries';
import { FILTERED_COUNTRIES } from '@fe/shared/PhoneConstants';
import { getCountryAcronymFromDialCode } from '@fe/shared/PhoneHelpers';
import { useFiltrationLogic } from '@fe/shared/UseFiltrationLogic';
import type { E_CountryDialCode } from '@fe/types/modules/localization/common/backend/enums/';
import type {
  T_CountryItem,
  T_CountryCodeSelectorContextProps,
} from '@fe/types/modules/localization/common/client/types';
import type { ComponentChildren } from 'preact';
import { createContext } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

export const CountryCodeSelectorContext = createContext<T_CountryCodeSelectorContextProps>({
  filteredCodes: undefined,
  setFilteredCodes: () => {},
  selectedFlagCode: undefined,
  setSelectedFlagCode: () => {},
  selectedCountryCode: undefined,
  setSelectedCountryCode: () => {},
  countryCodeToShow: QATAR_DIAL_CODE,
  flagCodeToShow: 'qa',
  inputRef: { current: null },
  focusOnSearchInput: () => {},
  setSearch: () => {},
  handleSelect: () => {},
  isOpen: false,
  setIsOpen: () => {},
  selectedCountry: undefined,
  setSelectedCountry: () => {},
});

export function CountryCodeSelectorProvider({ children }: { children: ComponentChildren }) {
  const [filteredCodes, setFilteredCodes] = useState<T_CountryItem[] | null>();
  const [selectedFlagCode, setSelectedFlagCode] = useState<string | undefined>();
  const [selectedCountryCode, setSelectedCountryCode] = useState<E_CountryDialCode | undefined>(
    undefined,
  );
  const [focusDelay, setFocusDelay] = useState(200);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<T_CountryItem | undefined>(undefined);

  const { onCountrySelect, onSearchValueChange } = useFiltrationLogic({
    setSelectedFlagCode,
    setSelectedCountryCode,
    setFilteredCodes,
    setSelectedCountry,
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const FILTERED_COUNTRIES_LIST = FILTERED_COUNTRIES();

  useEffect(() => {
    setFilteredCodes(FILTERED_COUNTRIES_LIST);
  }, []);

  const setSearch = (value: string) => {
    onSearchValueChange(value);
  };
  const handleSelect = (country: T_CountryItem) => {
    setSelectedCountry(country);
    onCountrySelect(country);
    setSelectedCountryCode(country.dialCode as E_CountryDialCode);
  };

  const countryCodeToShow = selectedCountryCode ?? QATAR_DIAL_CODE;

  const flagCodeToShow =
    selectedCountry?.isoCode?.toLocaleLowerCase() ??
    selectedFlagCode ??
    getCountryAcronymFromDialCode(countryCodeToShow) ??
    'qa';

  const focusOnSearchInput = (delay = 200) => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, delay);
  };

  useEffect(() => {
    focusOnSearchInput(focusDelay);
    setFocusDelay(filteredCodes?.length ? 5 : 300);
  }, [filteredCodes]);

  const contextValue = useMemo<T_CountryCodeSelectorContextProps>(
    () => ({
      filteredCodes,
      setFilteredCodes,
      selectedFlagCode,
      setSelectedFlagCode,
      selectedCountryCode,
      setSelectedCountryCode,
      countryCodeToShow,
      flagCodeToShow,
      inputRef,
      focusOnSearchInput,
      setSearch,
      handleSelect,
      isOpen,
      setIsOpen,
      selectedCountry,
      setSelectedCountry,
    }),
    [
      filteredCodes,
      selectedFlagCode,
      selectedCountryCode,
      countryCodeToShow,
      flagCodeToShow,
      isOpen,
    ],
  );

  return (
    <CountryCodeSelectorContext.Provider value={contextValue}>
      {children}
    </CountryCodeSelectorContext.Provider>
  );
}
